/**
 * @fileoverview Text Sanitization and Quick Reply Processing Utilities
 *
 * This module provides functions for sanitizing user input text and
 * processing quick reply responses from the bot.
 *
 * @module helpers/sanitizer
 * @author KeyReply Engineering Team
 *
 * @techdebt
 * - Function name is misspelled: "santizeText" should be "sanitizeText"
 * - Only handles markdown blockquote escaping - may need more sanitization
 */

import * as _ from "lodash-es";

/**
 * Sanitize user input text to prevent markdown rendering issues
 *
 * Escapes text that starts with ">" to prevent it from being
 * rendered as a markdown blockquote.
 *
 * @param {string} text - Raw user input text
 * @returns {string} Sanitized text with markdown characters escaped
 *
 * @techdebt
 * - Function name misspelled ("santize" vs "sanitize")
 * - Only handles blockquote - may need to handle other markdown
 * - Consider using a proper markdown escaping library
 *
 * @example
 * santizeText("> Hello") // Returns: "\\> Hello"
 * santizeText("Hello")   // Returns: "Hello"
 */
export function santizeText(text: string): string {
  // Check if text starts with ">" which would be rendered as blockquote
  const textAffectedByMarkdown = _.first(text) === ">"; // considered as block (in markdown)
  let sanitisedText = text;
  if (textAffectedByMarkdown) {
    // Escape the ">" character to prevent markdown rendering
    sanitisedText = `\\${text}`;
  }
  return sanitisedText;
}

/**
 * Quick reply item structure
 * @typedef {Object} QuickReply
 * @property {any} data - Payload data for the quick reply
 * @property {string} event - Event type (e.g., "faq_save_prediction_feedback")
 * @property {string} text - Display text for the quick reply
 */

/**
 * Split quick replies into regular replies and learning feedback replies
 *
 * Separates quick reply options based on whether they are for
 * feedback collection (learning) or regular user interaction.
 *
 * @param {Array<{data: any, event: string, text: string}>} quickReplies - Array of quick reply objects
 * @returns {[Array, Array]} Tuple of [regularReplies, learningReplies]
 *
 * Regular replies: All replies except feedback collection
 * Learning replies: Replies with event "faq_save_prediction_feedback"
 *
 * @example
 * splitQuickReplies([
 *   { text: "Yes", event: "confirm", data: {} },
 *   { text: "Was this helpful?", event: "faq_save_prediction_feedback", data: {} }
 * ])
 * // Returns: [[{text: "Yes", ...}], [{text: "Was this helpful?", ...}]]
 */
export function splitQuickReplies(quickReplies: { data: any; event: string; text: string }[]) {
  if (_.isEmpty(quickReplies)) {
    return [[], []];
  }
  return quickReplies.reduce(
    (
      acc: Array<{ data: any; event: string; text: string }[]>,
      curr: { data: any; event: string; text: string }
    ) => {
      // Separate feedback collection quick replies from regular ones
      if (curr.event !== "faq_save_prediction_feedback") {
        acc[0].push(curr); // Regular quick replies
      } else {
        acc[1].push(curr); // Learning/feedback quick replies
      }
      return acc;
    },
    [[], []]
  );
}
